"use client";

import cx from "classnames";
import ErrorComponent from "@/components/empty/ErrorComponent";
import { Tab, Tabs } from "@/components/headToHead";
import LoadingBackdrop from "@/components/loadingBackdrop/LoadingBackdrop";
import { SCREENS } from "@/constants/breakpoints";
import useGetHeadToHeadTeamStats from "@/utils/hooks/useGetHeadToHeadTeamStats";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import useMedia from "@/utils/hooks/useMedia";
import { TabContextProvider } from "@/utils/hooks/useTabsContext";
import { EmptyMessage } from "../../PlayerStats/PlayerProps/oddsUnavailable/OddsUnavailable";
import {
  AlternateTotalPoints,
  EvenOdd,
  LoneMarket,
  OverUnder,
  TouchdownScorerMarket,
} from "./BaseComponents";
import styles from "./teamGameOdds.module.scss";

export default function TeamGameOddsDisplay() {
  const { gameId, awayTeam, homeTeam } = useHeadToHeadContext();
  const awayTeamShortName = awayTeam?.shortName ?? "";
  const homeTeamShortName = homeTeam?.shortName ?? "";

  const { data, isLoading, isError, refetch } =
    useGetHeadToHeadTeamStats(gameId);

  const isMobile = useMedia(`(max-width: ${SCREENS.SM})`);

  const {
    homeTeamTotalPoints,
    awayTeamTotalPoints,
    alternateTotalPoints,
    totalPointsOddOrEven,
    totalPoints,
  } = data?.odds ?? {};
  const { anyTimeTouchdownScorer, firstTouchdownScorer } =
    data?.playerPropOdds ?? {};

  const teamOddsUnavailable =
    !isLoading &&
    !isError &&
    (homeTeamTotalPoints == null || homeTeamTotalPoints.length === 0) &&
    (awayTeamTotalPoints == null || awayTeamTotalPoints.length === 0) &&
    (alternateTotalPoints == null || alternateTotalPoints.length === 0) &&
    (totalPointsOddOrEven == null || totalPointsOddOrEven.length === 0);
  const touchdownOddsUnavailable =
    !isLoading &&
    !isError &&
    (anyTimeTouchdownScorer == null || anyTimeTouchdownScorer.length === 0) &&
    (firstTouchdownScorer == null || firstTouchdownScorer.length === 0);

  if (isError && !data) {
    return (
      <div className={styles.notLoadedContainer}>
        <ErrorComponent
          ctaButtonLabel="Retry"
          ctaButtonOnClick={() => refetch()}
        />
      </div>
    );
  } else if (isLoading && !data) {
    return (
      <div className={styles.notLoadedContainer}>
        <LoadingBackdrop loading />
      </div>
    );
  }

  return (
    <TabContextProvider defaultTab="totals">
      <div className={styles.tabsContainer}>
        <Tabs
          align={isMobile ? "center" : "left"}
          tabs={[
            { label: "Totals", value: "totals" },
            { label: "Touchdowns", value: "touchdowns" },
          ]}
        />
      </div>
      <Tab value="totals">
        <div style={{ position: "relative" }}>
          {teamOddsUnavailable && (
            <EmptyMessage className={styles.emptyMessage} />
          )}
          <div
            className={cx(
              styles.totals,
              teamOddsUnavailable && styles.oddsUnavailable
            )}
          >
            <LoneMarket>
              <OverUnder
                label={`${homeTeamShortName ?? "Home Team"} Total Points`}
                showHandicap
                selections={homeTeamTotalPoints}
              />
            </LoneMarket>
            <LoneMarket>
              <OverUnder
                label={`${awayTeamShortName ?? "Away Team"} Total Points`}
                showHandicap
                selections={awayTeamTotalPoints}
              />
            </LoneMarket>
            <LoneMarket leftLabel="Even" rightLabel="Odd">
              <EvenOdd
                label="Total Points Odd/Even"
                selections={totalPointsOddOrEven}
              />
            </LoneMarket>
          </div>
          <div
            className={cx(
              styles.alternateTotalPoints,
              teamOddsUnavailable && styles.oddsUnavailable
            )}
          >
            <AlternateTotalPoints
              totalPointsValue={totalPoints?.[0]?.handicap}
              markets={alternateTotalPoints}
            />
          </div>
        </div>
      </Tab>
      <Tab value="touchdowns">
        <div style={{ position: "relative" }}>
          {touchdownOddsUnavailable && (
            <EmptyMessage className={styles.emptyMessage} />
          )}
          <div
            className={cx(
              styles.touchdownMarketsContainer,
              touchdownOddsUnavailable && styles.oddsUnavailable
            )}
          >
            <TouchdownScorerMarket
              label="Any Time Touchdown Scorer"
              selections={anyTimeTouchdownScorer}
            />
            <TouchdownScorerMarket
              label="First Touchdown Scorer"
              selections={firstTouchdownScorer}
            />
          </div>
        </div>
      </Tab>
    </TabContextProvider>
  );
}
